import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  Card,
  ClickableCard,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
  CardFooter,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {
  FormLabelSmall,
  BodyMedium,
  SubTitleLarge,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {debounce} from 'lodash';
import {Button} from '@spaced-out/ui-design-system/lib/components';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@spaced-out/ui-design-system/lib/components/Modal';
import {
  Toast,
  ToastTitle,
  toastApi,
  TOAST_SEMANTIC,
  type ToastSemanticType,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {useApi} from 'src/hooks/useApi';

import {
  selectFilteredJobList,
  selectHasCrmPermission,
  selectUserInfo,
} from 'src/selectors/crm';
import {getFilteredJob} from 'src/action-creators/crm-new/index';
import {useSelector, useDispatch} from 'react-redux';
import {SimpleTypeahead} from '@spaced-out/ui-design-system/lib/components/Typeahead';
import AddReferralsPanel from 'src/components/crm/dashboard/referrals/add-referrals-panel/add-referrals-panel';
import {useCopyToClipboard} from '@spaced-out/ui-design-system/lib/hooks/useCopyToClipboard';
import {SemanticIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';

import css from './referrals.css';


type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const Referrals = () => {
  const {t} = useLabelTranslation();
  const userInfo = useSelector(selectUserInfo);
  const [shareReferralsModalOpen, setShareReferralsModalOpen] =
    React.useState(false);
  const [addReferralsPanelOpen, setAddReferralsPanelOpen] =
    React.useState(false);
  const addReferrals = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.ADD_REFERRALS,
  );
  const shareReferralLink = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.SHARE_REFERRAL_LINK,
  );
  return (
    <>
      {addReferralsPanelOpen && (
        <AddReferralsPanel
          isOpen={addReferralsPanelOpen}
          handleClose={() => {
            setAddReferralsPanelOpen(false);
          }}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon
              name="share"
              semantic="neutral"
              type="solid"
              size="small"
            />
            {t('Referral', 'Referral')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={css.innerCard}>
            <BodyMedium>{`${t(
              'Refer_candidates_with_or_without_a_jobit\'s_that_easy',
              'Refer candidates with or without a job—it\'s that easy',
            )}!`}</BodyMedium>
            <div className={css.buttonWrapper}>
              {addReferrals && (
                <Button
                  iconLeftName="add"
                  children={t('Add_a_referral', 'Add a referral')}
                  type="secondary"
                  onClick={() => setAddReferralsPanelOpen(true)}
                  classNames={{wrapper: css.button}}
                  size="small"
                />
              )}
              {shareReferralLink && (
                <Button
                  children={t('Share_Referral_Link', 'Share Referral Link')}
                  type="tertiary"
                  onClick={() => setShareReferralsModalOpen(true)}
                  classNames={{wrapper: css.button}}
                  size="small"
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <ShareReferralsModal
        isOpen={shareReferralsModalOpen}
        onClose={() => setShareReferralsModalOpen(false)}
      />
    </>
  );
};

const ShareReferralsModal = ({isOpen, onClose}: ModalProps) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const filteredJobList = useSelector(selectFilteredJobList);
  const [searchJobTextOrCode, setSearchJobTextOrCode] = React.useState('');
  const [selectedJobId, setSelectedJobId] = React.useState<any>(null);
  const [copiedText, copy] = useCopyToClipboard();

  const {result, isLoading} = useApi(
    `/api/get-user-referral-link/v1/${selectedJobId}`,
    {
      apiPath: '/trmapi',
      deps: [selectedJobId],
      shouldFetch: selectedJobId !== null && selectedJobId >= 0,
    },
  );

  // @ts-expect-error - TS2339 - Property 'referralLink' does not exist on type '{}'.
  const {referralLink} = result || {};
  const handleCopyLink = () => {
    copy(referralLink)
      .then((success) => {
        if (success) {
          toastApi.show(<SaveSuccessMessage />, {
            timeout: 2000,
          });
        } else {
          toastApi.show(<ErrorDangerMessage />, {
            timeout: 2000,
          });
        }
      })
      .catch((_error) => {
        toastApi.show(<ErrorDangerMessage />, {
          timeout: 2000,
        });
      });
  };
  const talentPoolOption = {
    key: 0,
    label: t('Talent_Pool', 'Talent Pool'),
    secondaryLabel: t(
      'This_option_will_not_link_to_any_job',
      'This option will not link to any job',
    ),
  } as const;

  const shareOnSocialMedia = (platform: string) => {
    const platformConfig = {
      facebook: {
        url: 'https://www.facebook.com/dialog/share',
        params: {
          app_id: '3030683300538528',
          display: 'popup',
          href: '',
        },
      },
      linkedin: {
        url: 'https://www.linkedin.com/shareArticle',
        params: {
          mini: 'true',
          url: '',
          source: 'Skillate',
        },
      },
      twitter: {
        url: 'https://twitter.com/intent/tweet',
        params: {
          url: '',
        },
      },
      whatsapp: {
        url: 'https://api.whatsapp.com/send',
        params: {
          text: '',
        },
      },
    } as const;

    const shareLink = encodeURIComponent(
      `${referralLink}&utm_source=${platform.toUpperCase()}&utm_medium=SOCIAL_MEDIA&source=${platform.toLowerCase()}`,
    );

    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly facebook: { readonly url: "https://www.facebook.com/dialog/share"; readonly params: { readonly app_id: "3030683300538528"; readonly display: "popup"; readonly href: ""; }; }; readonly linkedin: { ...; }; readonly twitter: { ...; }; readonly whatsapp: { ...; }; }'.
    const config = platformConfig[platform];
    const params = new URLSearchParams(config.params);
    const urlParam =
      platform === 'whatsapp'
        ? 'text'
        : platform === 'facebook'
        ? 'href'
        : 'url';
    params.set(urlParam, shareLink);

    const shareUrl = `${config.url}?${params.toString()}`;
    window.open(shareUrl, '_blank');
  };

  const jobOptions =
    // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'HIERARCHY_LEVEL[]'.
    filteredJobList?.content.map((job: any) => ({
      key: job.id,
      label: `${job.title} (${t('ID', 'ID')}: ${job.id})`,
      secondaryLabel: job.location,
    })) || [];

  const filteredJobListOptions = React.useMemo(() => {
    if (!searchJobTextOrCode) {
      return [talentPoolOption, ...jobOptions];
    }
    return [
      talentPoolOption,
      // @ts-expect-error - TS7006 - Parameter 'option' implicitly has an 'any' type.
      ...jobOptions.filter((option) =>
        option.label.toLowerCase().includes(searchJobTextOrCode.toLowerCase()),
      ),
    ];
  }, [searchJobTextOrCode, jobOptions]);

  const tempFilter = React.useMemo(
    () => ({
      publicAccess: false,
      titleOrCode: searchJobTextOrCode,
      templateName: '',
      pageSize: 50,
      page: 0,
      jobStatuses: ['OPEN'],
      referralActive: false,
      template: false,
    }),
    [searchJobTextOrCode],
  );

  const debouncedJobSearch = React.useCallback(
    debounce(() => {
      dispatch(getFilteredJob({...tempFilter}));
    }, 300),
    [dispatch, tempFilter],
  );
  React.useEffect(() => {
    debouncedJobSearch();
  }, [tempFilter, debouncedJobSearch]);

  const socialMediaButtons = [
    {platform: 'linkedin', icon: 'linkedin'},
    {platform: 'twitter', icon: 'x-twitter'},
    {platform: 'facebook', icon: 'facebook'},
    {platform: 'whatsapp', icon: 'whatsapp'},
  ];

  const handleClose = () => {
    setSelectedJobId(null);
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        classNames={{content: css.modalContent}}
        size="medium"
        customAnimation={{}}
      >
        <ModalHeader onCloseButtonClick={handleClose}>
          <SubTitleLarge>
            {t('Share_Referral_Link', 'Share Referral Link')}
          </SubTitleLarge>
        </ModalHeader>
        <ModalBody className={css.modalContent}>
          <SimpleTypeahead
            label={t('Select_a_Job', 'Select a Job')}
            placeholder={t(
              'Search_by_Job_Name_or_ID',
              'Search by Job Name or ID',
            )}
            typeaheadInputText={searchJobTextOrCode}
            options={filteredJobListOptions}
            onSearch={(e) => setSearchJobTextOrCode(e.target.value)}
            onSelect={(option) => {
              setSelectedJobId(Number(option.key));
            }}
            menuOpenOffset={0}
          />
          {selectedJobId !== undefined && selectedJobId !== null && (
            <>
              <div className={css.referralLinkField}>
                <Input
                  label={t('Referral_Link', 'Referral Link')}
                  value={referralLink}
                  disabled
                />
                <Button
                  type="tertiary"
                  iconLeftName="clipboard"
                  children={t('Copy', 'Copy')}
                  onClick={handleCopyLink}
                />
              </div>
              <div className={css.socialMediaLinks}>
                <FormLabelSmall>
                  {t('Or_share_via', 'Or share via')}
                </FormLabelSmall>
                <div className={css.gap}>
                  {socialMediaButtons.map(({platform, icon}) => (
                    <Button
                      key={platform}
                      type="tertiary"
                      iconLeftType="brands"
                      iconLeftName={icon}
                      onClick={() => shareOnSocialMedia(platform)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export const SaveSuccessMessage = () => {
  const {t} = useLabelTranslation();
  return (
    <Toast semantic={TOAST_SEMANTIC.success}>
      <ToastTitle>
        {t(
          'Referral_Link_copied_successfully',
          'Referral Link copied successfully',
        )}
      </ToastTitle>
    </Toast>
  );
};

export const ErrorDangerMessage = () => {
  const {t} = useLabelTranslation();
  return (
    <Toast semantic={TOAST_SEMANTIC.danger}>
      <ToastTitle>
        {t('Something_went_wrong', 'Something went wrong')}
      </ToastTitle>
    </Toast>
  );
};
